import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState, useEffect } from "react";
import { Section, SectionHeader } from "@/components/Section";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { Tilt3D } from "@/components/Tilt3D";
import { CRM_VIDEOS, CRM_CATEGORIES, CRM_LEARNING_TRACK, type CrmVideo } from "@/lib/crm-videos";
import { supabase } from "@/integrations/supabase/client";
import { StatusMonitor } from "@/components/StatusMonitor";
import manualApiOficial from "@/assets/manual-api-oficial-whatsapp.pdf.asset.json";

export const Route = createFileRoute("/suporte-crm")({
  head: () => ({
    meta: [
      { title: "Suporte e Treinamento CRM SVG | Tutoriais e IA de Ajuda" },
      { name: "description", content: "Central de suporte e treinamento do CRM SVG: pesquise tutoriais em vídeo, siga a trilha de estudos prática e tire dúvidas com a IA de suporte oficial." },
      { property: "og:title", content: "Suporte e Treinamento CRM SVG" },
      { property: "og:description", content: "Trilha de estudos, tutoriais em vídeo e IA para tirar dúvidas sobre o CRM SVG." },
      { property: "og:url", content: "/suporte-crm" },
    ],
    links: [{ rel: "canonical", href: "https://www.svgmultimidia.com.br/suporte-crm" }],
  }),
  component: SuporteCrmPage,
});

type ChatMsg = { role: "user" | "assistant"; content: string; videos?: CrmVideo[] };

function VideoCard({ video, compact = false, onExpand }: { video: CrmVideo; compact?: boolean; onExpand?: (v: CrmVideo) => void }) {
  return (
    <div className={`card-glass overflow-hidden ${compact ? "p-2" : ""}`}>
      <div className="relative aspect-video overflow-hidden rounded-xl border border-ink/10 shadow-[var(--shadow-soft)]">
        <iframe
          src={`https://www.youtube.com/embed/${video.id}`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          className="absolute inset-0 h-full w-full"
        />
        {onExpand && (
          <button
            type="button"
            onClick={() => onExpand(video)}
            aria-label="Assistir em tela maior"
            className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border border-ink/20 bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur hover:bg-black/80"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6"/><path d="M9 21H3v-6"/><path d="M21 3l-7 7"/><path d="M3 21l7-7"/></svg>
            Ampliar
          </button>
        )}
      </div>
      <div className="px-2 pb-1 pt-3">
        <span className="text-[10px] uppercase tracking-wider text-primary">{video.category}</span>
        <h3 className="mt-1 text-sm font-semibold leading-snug">{video.title}</h3>
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }: { video: CrmVideo; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={video.title}
    >
      <div
        className="relative w-[70vw] max-w-[1280px] max-h-[85vh] rounded-2xl border border-ink/10 bg-background shadow-2xl overflow-hidden"
        style={{ width: "min(70vw, 1280px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink/10">
          <div className="min-w-0">
            <span className="text-[10px] uppercase tracking-wider text-primary">{video.category}</span>
            <h3 className="truncate text-sm font-semibold">{video.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink/15 bg-ink/5 text-white hover:bg-ink/10"
          >
            ✕
          </button>
        </div>
        <div className="relative aspect-video w-full bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        </div>
      </div>
    </div>
  );
}

function SuporteCrmPage() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string | "Todos">("Todos");
  const [allVideos, setAllVideos] = useState<CrmVideo[]>(CRM_VIDEOS);
  const [allCategories, setAllCategories] = useState<readonly string[]>(CRM_CATEGORIES);
  const [expandedVideo, setExpandedVideo] = useState<CrmVideo | null>(null);

  // Carrega tutoriais do banco (sincronizados automaticamente da playlist) —
  // com fallback para o catálogo estático caso a tabela esteja vazia.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("crm_videos" as any)
          .select("id, title, category, keywords")
          .eq("hidden", false)
          .order("position", { ascending: true });
        if (error || cancelled || !data || (data as any[]).length === 0) return;
        const rows = (data as any[]).map((r) => ({
          id: r.id as string,
          title: r.title as string,
          category: (r.category as string) ?? "Geral",
          keywords: (r.keywords as string[]) ?? [],
        })) as CrmVideo[];
        setAllVideos(rows);
        // Mantém a ordem das categorias da trilha de estudos + acrescenta as novas
        const dbCats = Array.from(new Set(rows.map((r) => r.category)));
        const ordered = [
          ...CRM_CATEGORIES.filter((c) => dbCats.includes(c)),
          ...dbCats.filter((c) => !CRM_CATEGORIES.includes(c as any)),
        ];
        setAllCategories(ordered);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allVideos.filter((v) => {
      if (activeCat !== "Todos" && v.category !== activeCat) return false;
      if (!q) return true;
      return (
        v.title.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q) ||
        v.keywords.some((k) => k.toLowerCase().includes(q))
      );
    });
  }, [query, activeCat, allVideos]);

  // Chat
  const [messages, setMessages] = useState<ChatMsg[]>([
    {
      role: "assistant",
      content:
        "Olá! Eu sou a IA de suporte do **CRM SVG**. Pergunte qualquer dúvida sobre o sistema — agentes de IA, funil, follow-ups, disparos, agendamentos — e eu vou te orientar e enviar o vídeo tutorial certo quando existir.",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  async function sendMessage(e?: React.FormEvent) {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setError(null);
    const next: ChatMsg[] = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch("/api/chat-suporte", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ messages: next.map((m) => ({ role: m.role, content: m.content })) }),
      });
      if (!res.ok) {
        if (res.status === 429) throw new Error("Muitas perguntas seguidas. Aguarde alguns segundos e tente novamente.");
        if (res.status === 402) throw new Error("Créditos de IA esgotados. Avise o administrador.");
        throw new Error("Não consegui responder agora. Tente novamente em instantes.");
      }
      const data = (await res.json()) as { content: string; videos: CrmVideo[] };
      setMessages((m) => [...m, { role: "assistant", content: data.content || "Sem resposta.", videos: data.videos }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro inesperado.");
    } finally {
      setLoading(false);
    }
  }

  const suggestions = [
    "Como configurar um agente de IA?",
    "Como fazer um disparo em massa?",
    "Como configurar Follow-ups?",
    "Como integrar o Google Agenda?",
  ];

  return (
    <>
      <section className="hero-bg relative overflow-hidden">
        <div className="container-svg pt-12 pb-12 md:pt-20 md:pb-16">
          <Breadcrumbs items={[{ label: "Início", to: "/" }, { label: "CRM SVG", to: "/crm-yt" }, { label: "Suporte e Treinamento" }]} />
          <div className="mt-6 max-w-3xl">
            <span className="eyebrow">Central de Suporte · CRM SVG</span>
            <h1 className="mt-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              <span className="text-gradient">Treinamento, tutoriais e IA de ajuda</span> para o CRM SVG
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">
              Pesquise pelos tutoriais oficiais, siga a trilha prática de estudos e tire dúvidas com a IA de suporte — ela responde e envia o vídeo certo na hora.
            </p>
          </div>

          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-8 flex items-center gap-3 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] px-4 py-3 backdrop-blur-xl"
            role="search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden className="text-muted-foreground">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="search"
              placeholder="Pesquise: agente de IA, follow-up, disparo, kanban, Google Agenda…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              aria-label="Pesquisar tutoriais"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} className="text-xs text-muted-foreground hover:text-foreground">limpar</button>
            )}
          </form>

          <div className="mt-4 flex flex-wrap gap-2">
            {(["Todos", ...allCategories] as string[]).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCat(c)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-all ${activeCat === c ? "border-primary/60 bg-primary/15 text-foreground" : "border-ink/10 bg-ink/[0.045] text-muted-foreground hover:text-foreground"}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-8">
            <StatusMonitor />
          </div>

          <div className="mt-8 rounded-2xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent p-5 md:p-6 backdrop-blur-xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-400/30 bg-emerald-500/15 text-emerald-700">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                    <path d="M9 15h6"/>
                    <path d="M9 11h6"/>
                  </svg>
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700">Manual PDF</span>
                  <h3 className="mt-1 text-base font-semibold md:text-lg">Como configurar a API Oficial do WhatsApp direto pelo CRM</h3>
                  <p className="mt-1 text-xs text-muted-foreground md:text-sm">Passo a passo completo para conectar a API oficial (Meta Cloud) ao CRM SVG.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:shrink-0">
                <a
                  href={manualApiOficial.url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-ink/[0.06] px-4 py-2 text-xs font-medium text-foreground hover:bg-ink/[0.1]"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>
                  Abrir manual
                </a>
                <a
                  href={manualApiOficial.url}
                  download={manualApiOficial.original_filename}
                  className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/20 px-4 py-2 text-xs font-medium text-emerald-800 hover:bg-emerald-500/30"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Baixar PDF
                </a>
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 md:p-6 backdrop-blur-xl">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-primary/30 bg-primary/15 text-primary">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M12 7h.01"/>
                    <path d="M3 4h18"/>
                    <path d="M5 4v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4"/>
                    <path d="M9 14h6"/>
                  </svg>
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] uppercase tracking-wider text-primary">Documentação Oficial</span>
                  <h3 className="mt-1 text-base font-semibold md:text-lg">Consulte a documentação completa do CRM</h3>
                  <p className="mt-1 text-xs text-muted-foreground md:text-sm">Guias, parâmetros, endpoints e tudo sobre o funcionamento do sistema, direto no docs oficial.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:shrink-0">
                <a
                  href="https://docs.agendasistemacrm.com.br/"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/20 px-4 py-2 text-xs font-medium text-foreground hover:bg-primary/30"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5"/></svg>
                  Acessar documentação
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CHAT IA */}
      <Section>
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <div>
              <span className="eyebrow">IA de Suporte</span>
              <h2 className="mt-3 text-2xl font-bold md:text-3xl">Pergunte. A IA responde e te manda o vídeo certo.</h2>
              <p className="mt-3 text-sm text-muted-foreground">Treinada com todos os tutoriais do CRM SVG. Quando faz sentido, ela embute o vídeo exato sobre o assunto da sua dúvida.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button key={s} type="button" onClick={() => setInput(s)} className="rounded-full border border-ink/10 bg-ink/[0.04] px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">{s}</button>
                ))}
              </div>
              <div className="mt-4 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-4 text-xs text-muted-foreground">
                Suporte humano? Fale com a equipe no WhatsApp <a href="https://wa.me/555596852045?text=Ol%C3%A1%2C%20preciso%20de%20suporte%20com%20o%20CRM%20SVG" target="_blank" rel="noopener" className="text-primary underline-offset-4 hover:underline">(55) 9685-2045</a>.
              </div>
            </div>
          </Reveal>

          <Reveal delay={2}>
            <div className="card-glass flex h-[560px] flex-col p-0 overflow-hidden">
              <div className="border-b border-ink/10 bg-ink/[0.045] px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" /></span>
                  <p className="text-sm font-semibold">IA de Suporte CRM SVG</p>
                </div>
              </div>
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "ml-auto max-w-[85%]" : "max-w-[92%]"}>
                    <div className={`rounded-2xl p-3 text-sm leading-relaxed ${m.role === "user" ? "rounded-tr-sm bg-primary/15" : "rounded-tl-sm bg-ink/[0.05]"}`}>
                      <strong className={`block text-[10px] uppercase tracking-wider ${m.role === "user" ? "text-primary" : "text-muted-foreground"}`}>{m.role === "user" ? "Você" : "IA · CRM SVG"}</strong>
                      <div className="mt-1 whitespace-pre-wrap">{m.content}</div>
                    </div>
                    {m.videos && m.videos.length > 0 && (
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {m.videos.map((v) => <VideoCard key={v.id} video={v} compact onExpand={setExpandedVideo} />)}
                      </div>
                    )}
                  </div>
                ))}
                {loading && (
                  <div className="max-w-[60%] rounded-2xl rounded-tl-sm bg-ink/[0.05] p-3 text-sm">
                    <span className="inline-flex gap-1"><span className="h-2 w-2 animate-bounce rounded-full bg-primary" /><span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:120ms]" /><span className="h-2 w-2 animate-bounce rounded-full bg-primary [animation-delay:240ms]" /></span>
                  </div>
                )}
                {error && <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-700">{error}</div>}
                <div ref={chatEndRef} />
              </div>
              <form onSubmit={sendMessage} className="border-t border-ink/10 bg-ink/[0.045] p-3">
                <div className="flex items-end gap-2">
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                    placeholder="Digite sua dúvida sobre o CRM SVG…"
                    rows={2}
                    className="flex-1 resize-none rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-background/50 p-2 text-sm outline-none focus:border-primary/60"
                    aria-label="Pergunta para a IA de suporte"
                  />
                  <button type="submit" disabled={loading || !input.trim()} className="btn-primary disabled:opacity-50">Enviar</button>
                </div>
              </form>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* TRILHA DE ESTUDOS (única biblioteca, organizada por tópicos) */}
      <Section className="bg-[color:var(--color-sidebar)]">
        <SectionHeader
          eyebrow={query || activeCat !== "Todos" ? "Tutoriais por tópico" : "Trilha de estudos"}
          title={query || activeCat !== "Todos"
            ? <>Encontrados <span className="text-primary">{filtered.length}</span> vídeos organizados por tópico</>
            : <>Aprenda o CRM SVG <span className="text-primary">na ordem certa</span></>}
          description={query || activeCat !== "Todos"
            ? undefined
            : "Sequência prática recomendada para dominar o sistema do zero ao avançado."}
        />
        {filtered.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.045] p-8 text-center text-sm text-muted-foreground">
            Nenhum vídeo encontrado. Tente outra palavra-chave ou pergunte para a IA acima.
          </div>
        ) : (
          <ol className="mt-10 space-y-4">
            {(() => {
              const trackOrder = [
                ...CRM_LEARNING_TRACK.filter((c) => allCategories.includes(c)),
                ...allCategories.filter((c) => !CRM_LEARNING_TRACK.includes(c)),
              ];
              const visible = trackOrder
                .map((cat) => ({ cat, items: filtered.filter((v) => v.category === cat) }))
                .filter((g) => g.items.length > 0);
              return visible.map(({ cat, items }, idx) => (
                <Reveal as="li" key={cat} delay={((idx % 3) + 1) as 1 | 2 | 3}>
                  <details className="card-glass group" open={idx < 2 || !!query || activeCat !== "Todos"}>
                    <summary className="flex cursor-pointer items-center justify-between gap-4 list-none">
                      <div className="flex items-center gap-4">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 font-display text-base font-bold text-primary">{String(idx + 1).padStart(2, "0")}</span>
                        <div>
                          <h3 className="text-base font-semibold md:text-lg">{cat}</h3>
                          <p className="text-xs text-muted-foreground">{items.length} {items.length === 1 ? "vídeo" : "vídeos"}</p>
                        </div>
                      </div>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden className="transition-transform group-open:rotate-180"><path d="m6 9 6 6 6-6" /></svg>
                    </summary>
                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((v) => (
                        <Tilt3D key={v.id} max={5}><VideoCard video={v} onExpand={setExpandedVideo} /></Tilt3D>
                      ))}
                    </div>
                  </details>
                </Reveal>
              ));
            })()}
          </ol>
        )}
      </Section>

      <Section>
        <div className="card-glass flex flex-col items-start gap-6 p-8 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-xl font-bold md:text-2xl">Ainda com dúvida? Fale com o time</h3>
            <p className="mt-2 max-w-xl text-sm text-muted-foreground">Para casos específicos da sua operação, nosso suporte humano atende no WhatsApp.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href="https://wa.me/555596852045?text=Ol%C3%A1%2C%20preciso%20de%20suporte%20com%20o%20CRM%20SVG" target="_blank" rel="noopener" className="btn-primary">Suporte no WhatsApp</a>
            <Link to="/crm-yt" className="btn-ghost">Voltar ao CRM SVG</Link>
          </div>
        </div>
      </Section>
      {expandedVideo && <VideoModal video={expandedVideo} onClose={() => setExpandedVideo(null)} />}
    </>
  );
}